import { useState } from "react";
import { flushSync } from "react-dom";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { useAuth } from "src/domains/shared/hooks/user";
import withNotAuth from "../shared/hoc/auth/withNotAuth";
import useLocale from "../shared/hooks/localeHook/useLocale";
import UserConfirmationBox from "./UserConfirmationBox";
import notice from "../shared/utils/notice";
import { LoginForm } from "src/domains/shared/components/Login/LoginForm";
import { LoginBackground } from "src/domains/shared/components/Login/LoginBackground";
import { Alert } from "src/domains/shared/components/Alert";
import {
  LOGIN_REASON_SUPERSEDED,
  LOGIN_REASON_SUPERSEDED_PARTNER,
} from "src/domains/shared/utils/exclusiveLogin/loginSupersededHandler";
import { useTranslation } from "react-i18next";
import { testIds } from "src/test/testIds";
import { DeviceConflictDialog } from "./DeviceConflictDialog";
import { afterNextPaint } from "./afterNextPaint";

type LoginFormData = {
  email: string;
  password: string;
};

/**
 * 로그인 화면이 아는 밀려남 사유 → 안내 문구(`auth` 네임스페이스). 모르는 값·배열·없음은 안내하지 않는다.
 * 파트너 SSO 세션은 비밀번호가 없으므로 하이보드에서 다시 접속하라고 안내한다.
 */
const supersededNoticeKey = (reason: unknown) => {
  switch (reason) {
    case LOGIN_REASON_SUPERSEDED:
      return "message.supersededNotice";
    case LOGIN_REASON_SUPERSEDED_PARTNER:
      return "message.supersededPartnerNotice";
    default:
      return undefined;
  }
};

const LoginPage: NextPage = () => {
    const { t } = useTranslation("common");
  const router = useRouter();
  const [locale] = useLocale();

  const { signIn, error, deviceConflict } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // 정적 최적화 페이지라 첫 렌더의 query 는 비어 있다 — 준비된 뒤에만 사유를 읽는다.
  const noticeKey = router.isReady
    ? supersededNoticeKey(router.query.reason)
    : undefined;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // 진행 표시를 먼저 화면에 칠한 뒤 로그인한다 — 로그인 계산(SRP)이 메인 스레드를 붙잡아도 누른 직후부터 보이고 돈다.
  // `signIn` 은 다음 화면으로의 이동이 끝나야 끝나므로 진행 표시도 그때까지 이어진다.
  const startSignIn = async (email: string, password: string) => {
    flushSync(() => setIsLoading(true));
    await afterNextPaint();
    try {
      await signIn(email, password);
    } catch (err: any) {
      notice(
        "error",
        err?.message || t("auth.message.loginFailed", { ns: "common" })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (data: LoginFormData) =>
    startSignIn(data.email, data.password);

  // 미확인 계정 인증 상자가 확정을 마치면 같은 이메일·비밀번호로 로그인을 이어 간다 — 상자는 이 약속이 끝날 때까지 진행 중이다.
  const afterConfirm = async (result: boolean) => {
    if (result) await startSignIn(watch("email"), watch("password"));
  };

  return (
    <>
      <LoginBackground>
        <LoginForm
          onSubmit={handleSubmit(onSubmit)}
          register={register}
          errors={errors}
          error={error}
          isLoading={isLoading || deviceConflict.confirming}
          signUpHref="/signup"
          forgotPasswordHref="/changepassword"
          notice={
            noticeKey && (
              <Alert
                type="warning"
                message={t(noticeKey, { ns: "auth" })}
                data-testid={testIds.login.supersededNotice}
              />
            )
          }
        />
      </LoginBackground>

      {error?.name === "UserNotConfirmedException" && (
        <UserConfirmationBox
          username={watch("email")}
          onSuccess={afterConfirm}
        />
      )}

      <DeviceConflictDialog
        open={deviceConflict.open}
        confirming={deviceConflict.confirming}
        onConfirm={deviceConflict.confirm}
        onCancel={deviceConflict.cancel}
      />
    </>
  );
};

export default withNotAuth(LoginPage);
